/**
 * useCpiYoy — Round 96n (Jul 3 2026)
 *
 * Lightweight hook that returns the most-recent CPI YoY percentage
 * from `/api/macro` (Massive Fed API cache). Used by the "Real vs
 * Nominal Yield" toggle on the StockTable to inflation-adjust
 * dividend yields in one click.
 *
 * Design notes:
 *  - Single in-memory cache shared across every mount so the API is
 *    only hit once per page load (matches MacroContextStrip's own
 *    fetch, but this hook is standalone so tables can render on
 *    routes without the context strip).
 *  - Returns `null` while loading or on failure. Callers should
 *    disable the "Real" toggle when the value is null.
 *  - 5-minute stale window before a refetch is attempted — CPI is a
 *    monthly release, so the actual value never changes intraday.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STALE_MS = 5 * 60 * 1000;

let _cache = { value: null, at: 0, inflight: null };

export default function useCpiYoy() {
  const [value, setValue] = useState(_cache.value);

  useEffect(() => {
    const fresh = _cache.value != null && (Date.now() - _cache.at) < STALE_MS;
    if (fresh) { setValue(_cache.value); return; }

    if (!_cache.inflight) {
      _cache.inflight = axios
        .get(`${API}/macro`, { timeout: 3000 })
        .then((r) => {
          const cpi = r.data?.series?.inflation?.latest?.cpi_year_over_year;
          if (typeof cpi === 'number') {
            _cache.value = cpi;
            _cache.at = Date.now();
          }
          return _cache.value;
        })
        .catch(() => null)
        .finally(() => { _cache.inflight = null; });
    }
    _cache.inflight.then((v) => setValue(v));
  }, []);

  return value;
}
